import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import type { AutopilotConnectorCredentialSummary } from '@/lib/api/autopilot';
import {
  useAutopilotConnectorCredentialDetail,
  useUpsertAutopilotConnectorCredential,
} from '@/features/autopilot/hooks/useAutopilotCredentials';

type ConnectorType = AutopilotConnectorCredentialSummary['connector'];

type FieldType = 'text' | 'password' | 'textarea' | 'number';

type FieldConfig = {
  key: string;
  label: string;
  description?: string;
  placeholder?: string;
  optional?: boolean;
  type?: FieldType;
  autoComplete?: string;
  transform?: (value: string) => unknown;
};

const fieldConfigs: Record<ConnectorType, FieldConfig[]> = {
  google_ads: [
    { key: 'developerToken', label: 'Developer token', placeholder: 'e.g. 123abc456def789', type: 'text' },
    { key: 'oauthClientId', label: 'OAuth client ID', type: 'text', placeholder: 'xxxxx.apps.googleusercontent.com' },
    { key: 'oauthClientSecret', label: 'OAuth client secret', type: 'password', autoComplete: 'new-password' },
    { key: 'refreshToken', label: 'OAuth refresh token', type: 'password', autoComplete: 'new-password' },
    { key: 'loginCustomerId', label: 'Manager account ID', placeholder: 'Optional MCC ID', optional: true },
    { key: 'customerId', label: 'Customer account ID', placeholder: 'Google Ads account ID' },
    {
      key: 'conversionActionIds',
      label: 'Conversion action IDs',
      description: 'Comma separated list for each sandbox action.',
      placeholder: '1234567890,0987654321',
      transform: (value) =>
        value
          .split(',')
          .map((entry) => entry.trim())
          .filter((entry) => entry.length > 0),
    },
    { key: 'currencyCode', label: 'Currency code', placeholder: 'USD' },
    { key: 'timezone', label: 'Timezone', placeholder: 'America/Chicago' },
  ],
  meta_ads: [
    { key: 'accessToken', label: 'System user access token', type: 'password' },
    { key: 'pixelId', label: 'Pixel ID', placeholder: '111111111111111' },
    { key: 'appId', label: 'App ID', optional: true },
    { key: 'appSecret', label: 'App secret', type: 'password', optional: true },
    { key: 'datasetId', label: 'Dataset ID', optional: true },
    { key: 'testEventCode', label: 'Test event code', optional: true },
  ],
  waze_ads: [
    { key: 'apiKey', label: 'API key', type: 'password' },
    { key: 'accountId', label: 'Account ID', optional: true },
    { key: 'notes', label: 'Notes', optional: true },
  ],
  nextdoor_ads: [
    { key: 'apiKey', label: 'API key', type: 'password' },
    { key: 'accountId', label: 'Account ID', optional: true },
    { key: 'notes', label: 'Notes', optional: true },
  ],
  sms_campaign: [
    { key: 'accountSid', label: 'Account SID', placeholder: 'ACxxxxxxxxxxxxxxxxxxxxxxxxxxxx' },
    { key: 'authToken', label: 'Auth token', type: 'password', autoComplete: 'new-password' },
    { key: 'messagingServiceSid', label: 'Messaging Service SID', optional: true },
    { key: 'fromNumber', label: 'From number', optional: true, placeholder: '+15551234567' },
    {
      key: 'unitCostCents',
      label: 'Unit cost (cents)',
      type: 'number',
      optional: true,
      transform: (value) => Number.parseInt(value, 10),
    },
    {
      key: 'initialCreditsCents',
      label: 'Initial credit (cents)',
      type: 'number',
      optional: true,
      transform: (value) => Number.parseInt(value, 10),
    },
  ],
  email_campaign: [
    { key: 'serverToken', label: 'Server token', type: 'password', autoComplete: 'new-password' },
    { key: 'messageStream', label: 'Message stream', optional: true, placeholder: 'transactional' },
    { key: 'fromAddress', label: 'From address', optional: true, placeholder: 'transactions@restaurant.com' },
    { key: 'webhookSecret', label: 'Webhook secret', optional: true, type: 'password' },
    {
      key: 'unitCostCents',
      label: 'Unit cost (cents)',
      type: 'number',
      optional: true,
      transform: (value) => Number.parseInt(value, 10),
    },
    {
      key: 'initialCreditsCents',
      label: 'Initial credit (cents)',
      type: 'number',
      optional: true,
      transform: (value) => Number.parseInt(value, 10),
    },
  ],
};

const connectorLabels: Record<ConnectorType, string> = {
  google_ads: 'Google Ads',
  meta_ads: 'Meta Ads',
  waze_ads: 'Waze Ads',
  nextdoor_ads: 'Nextdoor Ads',
  sms_campaign: 'SMS Campaigns',
  email_campaign: 'Email Campaigns',
};

const defaultFieldValue = (field: FieldConfig): string => {
  if (field.type === 'number') {
    return '';
  }
  return '';
};

type AutopilotConnectorCredentialDialogProps = {
  connector: ConnectorType | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  environment?: 'sandbox' | 'production';
  onSaved?: () => void;
};

const AutopilotConnectorCredentialDialog = ({
  connector,
  open,
  onOpenChange,
  environment = 'production',
  onSaved,
}: AutopilotConnectorCredentialDialogProps) => {
  const { t } = useTranslation('autopilot');
  const { toast } = useToast();
  const [values, setValues] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const detailQuery = useAutopilotConnectorCredentialDetail(open ? connector : null, environment);
  const upsertMutation = useUpsertAutopilotConnectorCredential();

  const fields = useMemo(() => (connector ? fieldConfigs[connector] : []), [connector]);
  const metadataFields = (detailQuery.data?.metadata?.fields as Record<string, unknown> | undefined) ?? {};

  useEffect(() => {
    if (open && connector) {
      const nextValues: Record<string, string> = {};
      for (const field of fieldConfigs[connector]) {
        nextValues[field.key] = defaultFieldValue(field);
      }
      setValues(nextValues);
      setSubmitError(null);
    }
  }, [open, connector]);

  const handleChange = (field: string, value: string) => {
    setValues((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!connector) {
      return;
    }
    try {
      const payload: Record<string, unknown> = {};
      for (const field of fieldConfigs[connector]) {
        const rawValue = (values[field.key] ?? '').trim();
        if (!rawValue && !field.optional) {
          throw new Error(`Field "${field.label}" is required.`);
        }
        if (!rawValue) {
          continue;
        }
        const transformed = field.transform ? field.transform(rawValue) : rawValue;
        payload[field.key] = transformed;
      }

      await upsertMutation.mutateAsync({
        connector,
        environment,
        credentials: payload,
      });

      toast({
        title: t('prototype.credentials.saveSuccess', { defaultValue: 'Credentials updated' }),
      });

      if (onSaved) {
        onSaved();
      }

      onOpenChange(false);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : t('prototype.credentials.saveError', { defaultValue: 'Unable to save credentials' });
      setSubmitError(message);
      toast({
        title: t('prototype.credentials.saveErrorTitle', { defaultValue: 'Unable to save credentials' }),
        description: message,
        variant: 'destructive',
      });
    }
  };

  const title = connector ? connectorLabels[connector] : '';
  const isLoading = detailQuery.isLoading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('prototype.credentials.title', { defaultValue: 'Configure connector credentials' })}</DialogTitle>
          <DialogDescription>
            {connector
              ? t('prototype.credentials.subtitle', {
                  defaultValue: 'Provide credentials for {{connector}} ({{environment}})',
                  connector: title,
                  environment,
                })
              : null}
          </DialogDescription>
        </DialogHeader>

        {connector ? (
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <Badge variant="outline">{title}</Badge>
                <Badge
                  variant={detailQuery.data?.credentialStatus === 'active' ? 'secondary' : 'outline'}
                >
                  {detailQuery.data?.credentialStatus ?? 'none'}
                </Badge>
                {detailQuery.data?.hasCredentials ? (
                  <Badge variant="outline" className="gap-1">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                    {t('prototype.credentials.statusConfigured', { defaultValue: 'Configured' })}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="gap-1">
                    <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                    {t('prototype.credentials.statusMissing', { defaultValue: 'Missing' })}
                  </Badge>
                )}
              </div>
              {detailQuery.data?.provider ? (
                <div className="rounded-md bg-muted/50 p-3 text-sm">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="font-medium">
                      {detailQuery.data.provider.channel === 'sms'
                        ? t('prototype.credentials.provider.sms', { defaultValue: 'SMS credits' })
                        : t('prototype.credentials.provider.email', { defaultValue: 'Email credits' })}
                    </span>
                    <span>
                      {t('prototype.credentials.provider.balance', {
                        defaultValue: 'Balance: {{cents}}¢',
                        cents: detailQuery.data.provider.creditBalanceCents ?? 0,
                      })}
                    </span>
                    <span>
                      {t('prototype.credentials.provider.unitCost', {
                        defaultValue: 'Unit cost: {{cents}}¢',
                        cents: detailQuery.data.provider.unitCostCents ?? 0,
                      })}
                    </span>
                  </div>
                </div>
              ) : null}
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : (
              <div className="space-y-4">
                {fields.map((field) => (
                  <div key={field.key} className="space-y-1.5">
                    <Label htmlFor={`credential-${field.key}`} className="flex items-center gap-2">
                      {field.label}
                      {metadataFields[field.key] ? (
                        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" aria-hidden="true" />
                      ) : null}
                      {field.optional ? (
                        <span className="text-xs text-muted-foreground">
                          {t('prototype.credentials.optional', { defaultValue: 'Optional' })}
                        </span>
                      ) : null}
                    </Label>
                    {field.type === 'textarea' ? (
                      <Textarea
                        id={`credential-${field.key}`}
                        value={values[field.key] ?? ''}
                        onChange={(event) => handleChange(field.key, event.target.value)}
                        placeholder={field.placeholder}
                      />
                    ) : (
                      <Input
                        id={`credential-${field.key}`}
                        type={field.type === 'number' ? 'number' : field.type === 'password' ? 'password' : 'text'}
                        value={values[field.key] ?? ''}
                        onChange={(event) => handleChange(field.key, event.target.value)}
                        placeholder={field.placeholder}
                        autoComplete={field.autoComplete}
                      />
                    )}
                    {field.description ? (
                      <p className="text-xs text-muted-foreground">{field.description}</p>
                    ) : null}
                  </div>
                ))}
              </div>
            )}

            {submitError ? (
              <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {submitError}
              </div>
            ) : null}

            <Separator />
            <DialogFooter className="flex flex-row justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('prototype.credentials.cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button type="submit" disabled={upsertMutation.isPending || isLoading}>
                {upsertMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                ) : null}
                {t('prototype.credentials.save', { defaultValue: 'Save credentials' })}
              </Button>
            </DialogFooter>
          </form>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default AutopilotConnectorCredentialDialog;
